import { useState, useEffect } from 'react';
import { fetchConfigData, fetchData } from '../services/dataApi';

// Hardcoded fallback — used when the JSON hasn't loaded yet or fetch fails
const DEFAULT_CONFIG = {
  nativeLanguageList: null,
  targetLanguageList: null,
  learningPathTitle: '',
  learningPathSubtitle: '',
  audioLangs: ['te', 'en', 'es'],
  level: [],
  langsWithoutArticles: ['hi', 'kn', 'ml', 'pl', 'ru', 'ta', 'te', 'zh'],
  homeFeatures: [],
  learningSteps: [],
};

// Module-level caches — base config is shared; journeys are native-language specific.
let _baseConfig = null;
let _basePromise = null;
const _configsByNative = new Map();
const _promisesByNative = new Map();

function normalizeNativeCode(code) {
  const normalized = String(code || 'en').trim().toLowerCase();
  return /^[a-z]{2,3}$/.test(normalized) ? normalized : 'en';
}

function loadBaseConfig() {
  if (_basePromise) return _basePromise;
  _basePromise = fetchConfigData('app_config.json')
    .then((data) => {
      _baseConfig = { ...DEFAULT_CONFIG, ...data };
      return _baseConfig;
    })
    .catch(() => DEFAULT_CONFIG)
    .finally(() => { _basePromise = null; });
  return _basePromise;
}

function journeyContent(data) {
  const title = data?.learningSteps?.title;
  const subtitle = data?.learningSteps?.subtitle;
  const steps = data?.learningSteps?.steps;
  if (typeof title !== 'string' || !title.trim()) {
    throw new Error('User journey has no learningSteps.title');
  }
  if (typeof subtitle !== 'string' || !subtitle.trim()) {
    throw new Error('User journey has no learningSteps.subtitle');
  }
  if (!Array.isArray(steps) || steps.length === 0) {
    throw new Error('User journey has no learningSteps.steps');
  }
  return { title, subtitle, steps };
}

function localizedString(value, fallback) {
  return typeof value === 'string' && value.trim() ? value : fallback;
}

function mergeJourneyContent(englishData, localizedData) {
  const english = journeyContent(englishData);
  const localized = localizedData?.learningSteps;
  if (!localized || typeof localized !== 'object') return english;
  const localizedByKey = new Map(
    (Array.isArray(localized.steps) ? localized.steps : []).map((step) => [
      step.code || step.step,
      step,
    ]),
  );
  return {
    title: localizedString(localized.title, english.title),
    subtitle: localizedString(localized.subtitle, english.subtitle),
    steps: english.steps.map((step) => {
      const overlay = localizedByKey.get(step.code || step.step) || {};
      return {
        ...step,
        ...overlay,
        code: localizedString(overlay.code, step.code),
        label: localizedString(overlay.label, step.label),
        desc: localizedString(overlay.desc, step.desc),
        icon: localizedString(overlay.icon, step.icon),
      };
    }),
  };
}

// user-journey lives under data-common — it's shared identically across
// every curriculum, not per-target-home content.
async function loadJourney(nativeCode) {
  try {
    const englishData = await fetchData('user-journey/en.json');
    if (nativeCode === 'en') return journeyContent(englishData);
    const localizedData = await fetchData(`user-journey/${nativeCode}.json`)
      .catch(() => null);
    return mergeJourneyContent(englishData, localizedData);
  } catch {
    // Use the bundled empty fallback only when canonical English is unavailable.
  }
  return {
    title: DEFAULT_CONFIG.learningPathTitle,
    subtitle: DEFAULT_CONFIG.learningPathSubtitle,
    steps: DEFAULT_CONFIG.learningSteps,
  };
}

function loadConfig(nativeCode) {
  if (_configsByNative.has(nativeCode)) {
    return Promise.resolve(_configsByNative.get(nativeCode));
  }
  if (_promisesByNative.has(nativeCode)) return _promisesByNative.get(nativeCode);

  const request = Promise.all([loadBaseConfig(), loadJourney(nativeCode)])
    .then(([baseConfig, journey]) => {
      const config = {
        ...baseConfig,
        learningPathTitle: journey.title,
        learningPathSubtitle: journey.subtitle,
        learningSteps: journey.steps,
      };
      _configsByNative.set(nativeCode, config);
      return config;
    })
    .finally(() => _promisesByNative.delete(nativeCode));
  _promisesByNative.set(nativeCode, request);
  return request;
}

export function useAppConfig(nativeLanguage = 'en') {
  const nativeCode = normalizeNativeCode(nativeLanguage);
  const [config, setConfig] = useState(
    () => _configsByNative.get(nativeCode) ?? DEFAULT_CONFIG,
  );

  useEffect(() => {
    let active = true;
    const load = () => loadConfig(nativeCode).then((loaded) => {
      if (active) setConfig(loaded);
    });
    setConfig(_configsByNative.get(nativeCode) ?? _baseConfig ?? DEFAULT_CONFIG);
    load();
    const refresh = () => {
      _configsByNative.delete(nativeCode);
      load();
    };
    window.addEventListener('online', refresh);
    return () => {
      active = false;
      window.removeEventListener('online', refresh);
    };
  }, [nativeCode]);

  return config;
}
